import { ToolbarIconButton } from "@-/dev.improve.haniverse.components.ui.toolbar.button.icon";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import {
  LearnDeckHistoryContext,
  type LearnDeckSavedActionUndo,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";

import { useHistoryStepSpinner } from "#use-history-step-spinner";

/**
 * Steps history back — or, while a saved action's successor has failed to load, rolls that action
 * back instead: it is not in history yet, and a stuck card must never leave Undo unavailable.
 *
 * The card Undo brings back is usually still mounted, so it slides in as soon as the rollback is
 * written, and Undo only shows its spinner when the wait lasts: the card has to be mounted (or its
 * data loaded) again before it can be drawn.
 */
export function UndoReviewButton({
  isTransitioning,
  savedActionUndo,
}: {
  isTransitioning: boolean;
  savedActionUndo: LearnDeckSavedActionUndo | null;
}) {
  const [canGoBack, goBack, isHistoryPending, pendingStep] = useContextSelectors(
    LearnDeckHistoryContext,
    (c) => c.canGoBack,
    (c) => c.goBack,
    (c) => c.isPending,
    (c) => c.pendingStep,
  );
  const pending = savedActionUndo?.isPending ?? isHistoryPending;
  const showsSpinner = useHistoryStepSpinner(
    savedActionUndo === null ? isHistoryPending && pendingStep !== "forward" : pending,
  );
  return (
    <ToolbarIconButton
      accessibilityLabel="Undo"
      className="ml-0 border-0 bg-transparent shadow-none"
      disabled={savedActionUndo === null ? !canGoBack || pending || isTransitioning : pending}
      iconSize={20}
      isLoading={showsSpinner}
      name="IoMdUndo"
      onPress={savedActionUndo?.undo ?? goBack}
      size="menu"
      testID="learn-undo-review-button"
    />
  );
}
